import axios, { AxiosResponse, getAdapter, InternalAxiosRequestConfig } from 'axios';
import { TokenManager } from '../token-manager';
import { broadcastSessionInvalid } from '../sessionSync';
import { Cache } from '../cache';

// API Configuration
export const API_BASE_URL = process.env['REACT_APP_API_URL'] || 'http://localhost:8000/api';

/**
 * In-flight GET deduplication.
 *
 * Every page load fires 30-58 API calls, and measurement on production
 * showed a third of them are the SAME GET issued 2-3x concurrently by
 * independent contexts/components (dashboard_stats, companies/for-filter,
 * settings, unread_count, feature-flags...). Behind 8 gunicorn threads the
 * duplicates don't just waste compute — they lengthen the queue every other
 * request waits in.
 *
 * Identical concurrent GETs share one network request. The map entry clears
 * as soon as the request settles, so sequential refreshes (polling, manual
 * refresh buttons) still hit the server every time. Duplicate consumers get
 * a structuredClone of the payload so one consumer mutating its copy (e.g.
 * sorting rows in place) can never corrupt another's.
 *
 * Blob/arraybuffer downloads (exports) are excluded — their consumers
 * stream the body and must never share it.
 */
const inflightGets = new Map<string, Promise<AxiosResponse>>();

/**
 * Short-lived response cache for stable config endpoints.
 *
 * The boot burst also re-fetches the same config SEQUENTIALLY (context A's
 * call completes, then context B asks again 200ms later), which in-flight
 * dedupe alone can't collapse. These endpoints are safelisted because their
 * payload is per-session-stable; the cache is tiny (5s) and is cleared on
 * ANY mutating request, so a save-then-refetch can never read a stale copy.
 */
const MICRO_CACHE_TTL_MS = 5000;
const MICRO_CACHEABLE = new RegExp(
  [
    'feature-flags/$',
    'preferences/$',
    'preferences/choices/$',
    'preferences/view-effective/$',
    'exchange-rates/latest/$',
    'settings/display/$',
    'settings/performance/$',
    'sites/accessible/$',
    'companies/accessible/$',
    'companies/for-filter/$',
    'view-context/$',
  ].join('|'),
);
const microCache = new Map<string, { expires: number; response: AxiosResponse }>();

/**
 * Read-only POSTs that are safe to dedupe in flight. The batched tab-count
 * endpoints take their (long) param sets in the body rather than the query
 * string, but they read exactly like a GET: same body, same answer, no side
 * effects. Without this the dev-mode StrictMode double-invoke (and any
 * consumer whose params flap on mount) issues two identical count requests
 * where the GET path would have issued one.
 */
const READ_ONLY_POST = /\/tab-counts\/?$/;

function dedupeKey(config: InternalAxiosRequestConfig): string | null {
  const method = (config.method || 'get').toLowerCase();
  const urlPath = (config.url || '').split('?')[0];
  if (method === 'post' && READ_ONLY_POST.test(urlPath)) {
    const body = typeof config.data === 'string' ? config.data : JSON.stringify(config.data ?? '');
    return `POST|${config.baseURL || ''}|${config.url || ''}|${body}`;
  }
  if (method !== 'get') return null;
  if (config.responseType && config.responseType !== 'json') return null;
  const params = config.params ? JSON.stringify(config.params) : '';
  return `${config.baseURL || ''}|${config.url || ''}|${params}`;
}

const defaultAdapter = getAdapter(axios.defaults.adapter);

async function dedupingAdapter(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const key = dedupeKey(config);
  if (key === null) {
    // Any mutation invalidates the config cache wholesale — cheap, and it
    // guarantees write-then-read freshness without per-endpoint mapping.
    microCache.clear();
    return defaultAdapter(config);
  }

  const cacheable = MICRO_CACHEABLE.test((config.url || '').split('?')[0]);
  if (cacheable) {
    const hit = microCache.get(key);
    if (hit && hit.expires > Date.now()) {
      return { ...hit.response, data: structuredClone(hit.response.data) };
    }
  }

  const inflight = inflightGets.get(key);
  if (inflight) {
    const shared = await inflight;
    return { ...shared, data: structuredClone(shared.data) };
  }

  const request = defaultAdapter(config)
    .then((response) => {
      if (cacheable && response.status === 200) {
        microCache.set(key, { expires: Date.now() + MICRO_CACHE_TTL_MS, response });
      }
      return response;
    })
    .finally(() => {
      inflightGets.delete(key);
    });
  inflightGets.set(key, request);
  return request;
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 second timeout (remote DB)
  adapter: dedupingAdapter,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and user-scoped cache busting
api.interceptors.request.use(
  (config) => {
    // Use TokenManager for secure token retrieval
    const token = TokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    // Include CSRF token for session auth compatibility
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1];
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    // CR-4713 round 2 rework: Send real admin user ID during impersonation
    // for the audit trail. The previous read used obsolete localStorage keys
    // ('original_token', 'original_user_id') that TokenManager no longer
    // writes — it stores the original user object in sessionStorage as
    // 'helix_original_user' and the token as 'helix_original_token'. So the
    // X-Impersonated-By header was never sent in production, the backend's
    // _get_acting_as_user() always returned None, and acting_as_user stayed
    // null on every changelog row. Pull from TokenManager's source of truth.
    if (TokenManager.isImpersonating()) {
      const originalUser = TokenManager.getOriginalUser<{ id?: number }>();
      if (originalUser?.id) {
        config.headers['X-Impersonated-By'] = String(originalUser.id);
      }
    }

    // Add user ID to calendar GET requests for user-scoped browser caching
    // This ensures different users get fresh data (different URL = different cache entry)
    // while same user still benefits from browser caching
    if (config.method?.toLowerCase() === 'get' && config.url?.includes('/calendar/')) {
      const user = TokenManager.getUser<{ id: number }>();
      if (user?.id) {
        config.params = {
          ...config.params,
          _uid: user.id,
        };
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Remember which token died and tell the other tabs, BEFORE clearing:
      // otherwise a sibling hands the same dead token straight back over
      // the session handoff and this tab reloads /login forever.
      const deadToken = TokenManager.getToken();
      if (deadToken) {
        TokenManager.markTokenDead(deadToken);
        broadcastSessionInvalid(deadToken);
      }
      // Clear token, cached data, and redirect to login
      TokenManager.clearAll();
      Cache.clearAll();
      Cache.setUserScope(null);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Generic Types
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Re-export AxiosResponse for convenience
export type { AxiosResponse };

/**
 * Fetch EVERY row of a paginated endpoint by walking `next` pages.
 *
 * The backend clamps `page_size` to `max_page_size` (100 on the default
 * StandardResultsSetPagination), so a single request with a large
 * `page_size` silently truncates — rows past the cap never reach the
 * client, which breaks any screen that filters, aggregates or exports
 * client-side. Use this wherever the consumer needs the full dataset.
 *
 * @param fetchPage - Fetches one page (1-based). Usually
 *   `(page) => api.get(url, { params: { ...params, page } })`.
 *   Endpoints that return a plain array (unpaginated) are handled: the
 *   array is returned after the first call.
 * @param maxPages - Safety valve against runaway loops (default 50).
 * @returns All rows across every page, in order.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<AxiosResponse<Partial<PaginatedResponse<T>> | T[]>>,
  maxPages = 50,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 1; page <= maxPages; page += 1) {
    const { data } = await fetchPage(page);
    if (Array.isArray(data)) {
      return data;
    }
    all.push(...(data.results || []));
    if (!data.next) break;
  }
  return all;
}

export default api;
